
import theme from '@/assets/theme.scss';
import FormatterService from "@/service/FormatterService";
import ChartService from '@/service/ChartService';

export default {
    name: 'SectorBarChart',

    data() {
        return {
            rawData: [],
            chartData: [],
            chartOptions: {},
            isInitializing: true,
            isLoading: false,
        };
    },

    computed: {
        intervals() {
            return [
                {
                    interval: 'day',
                    text: this.$t('common.intervals.day')
                },
                {
                    interval: 'week',
                    text: this.$t('common.intervals.week')
                },
                {
                    interval: 'month',
                    text: this.$t('common.intervals.month')
                },
                {
                    interval: '3m',
                    text: this.$t('common.intervals.threeMonths')
                },
                {
                    interval: '6m',
                    text: this.$t('common.intervals.sixMonths')
                },
                {
                    interval: 'ytd',
                    text: this.$t('common.intervals.ytd')
                },
                {
                    interval: 'year',
                    text: this.$t('common.intervals.year')
                },
                {
                    interval: '2y',
                    text: this.$t('common.intervals.twoYears')
                },
                {
                    interval: '3y',
                    text: this.$t('common.intervals.threeYears')
                },
                {
                    interval: '4y',
                    text: this.$t('common.intervals.fourYears')
                },
                {
                    interval: 'fiveyears',
                    text: this.$t('common.intervals.fiveYears')
                },
                {
                    interval: '10y',
                    text: this.$t('common.intervals.tenYears')
                },
                {
                    interval: '20y',
                    text: this.$t('common.intervals.twentyYears')
                },
                {
                    interval: 'max',
                    text: this.$t('common.intervals.max')
                }
            ]
        },
    },

    watch: {
        '$store.state.preferences.sectorBarChartInterval': {
            handler() {
                this.refresh();
            },
        }
    },

    created() {
        this.format = new FormatterService();
        this.chartService = new ChartService(this.$axios);
        this.colors = [
            theme['chart-color-1'],
            theme['chart-color-2'],
            theme['chart-color-3'],
            theme['chart-color-4'],
            theme['chart-color-5'],
            theme['chart-color-5'],
            theme['chart-color-6'],
            theme['chart-color-7'],
            theme['chart-color-8'],
            theme['chart-color-9'],
            theme['chart-color-10'],
            theme['chart-color-11'],
            theme['chart-color-12'],
            theme['chart-color-13'],
            theme['chart-color-14'],
            theme['chart-color-15'],
            theme['chart-color-16'],
        ];
    },

    mounted() {
        this.refresh();
    },

    beforeDestroy() {
        this.cancelActiveRequest();
    },

    methods: {
        cancelActiveRequest() {
            if (this.activeRequestGetChartData) {
                this.activeRequestGetChartData.cancel();
                this.activeRequestGetChartData = null;
            }
        },

        onIntervalClick(newInterval) {
            this.$store.commit('preferences/setSectorBarChartInterval', newInterval);
        },

        async refresh() {            
            await this.fetchData();
            await this.$nextTick();
            this.makeOptions();
            this.prepareData();
        },

        prepareData() {
            this.chartData = [
                [
                    'Sector',
                    'Change Percentage',
                    { role: 'style' },
                    { 'type': 'string', 'role': 'tooltip', 'p': { 'html': true } },
                ],
                ...this.getFormattedData(),
            ];
        },

        getPerformanceValue(graphsData) {
            return graphsData.map(item => {
                return {
                    id: item.isin,
                    name: this.$t(item.graphName),
                    value: item.datapoints.length > 0 ? item.datapoints[item.datapoints.length - 1].nr : 0,
                };
            });
        },

        async fetchData() {
            this.isLoading = true;
            this.cancelActiveRequest();
            this.activeRequestGetChartData = this.$axios.CancelToken.source();
            const data = {
                source: 'share',
                identifier: null,
                interval: this.$store.state.preferences.sectorBarChartInterval,
                axiosSource: this.activeRequestGetChartData,
                sourceIdentifier: 'ZZSECTORBMAT', // Basic Materials
                comparisons: [
                    'ZZSECTORCOMM', // Communication Services
                    'ZZSECTORCCYC', // Consumer Cyclical
                    'ZZSECTORCDEF', // Consumer Defensive
                    'ZZSECTORENER', // Energy
                    'ZZSECTORFINS', // Financial Services
                    'ZZSECTORHLTH', // Healthcare
                    'ZZSECTORINDS', // Industrials
                    'ZZSECTORREAL', // Real Estate
                    'ZZSECTORTECH', // Technology
                    'ZZSECTORUTIL', // Utilities
                ],
            };

            try {
                const res = await this.chartService.get(data, this.$t);
                this.rawData = this.getPerformanceValue(res.graphs);
                this.isInitializing = false;
                this.isLoading = false;
                this.activeRequestGetChartData = null;
            } catch (error) {
                if (this.$axios.isCancel(error)) {
                    return;
                }
                this.isInitializing = false;
                this.isLoading = false;
                this.activeRequestGetChartData = null;
            }

        },

        getFormattedData() {
            return this.rawData.map((item, index) => {                
                return [
                    this.$t(item.name),
                    item.value,
                    this.colors[index % this.colors.length],
                    this.$refs[`tooltip-${item.id}`]?.[0]?.innerHTML
                ];
            });
        },

        makeOptions() {
            const canvas = document.createElement("canvas")
            const ctx = canvas.getContext('2d')
            ctx.font = "12px Albert Sans"
            const labelWidths = this.rawData.map(item => ctx.measureText(this.format.percent(item.value)).width)
            const labelMaxWidth = Math.max(...labelWidths)
            this.chartOptions = {
                fontName:'Albert Sans',
                chartArea: {
                    left: Math.ceil(labelMaxWidth) + 8,
                    width: '100%',
                },
                height: 360,
                bar: { groupWidth: "45%" },
                legend: 'none',
                vAxis: {
                    format: '#\'%\'',
                    baseline: 0,
                },
                tooltip: {
                    isHtml: true,
                },
                hAxis: this.$vuetify.breakpoint.mdAndUp ? {
                    // Force text to be vertical and wrap
                    textStyle: {
                        fontSize: 14,
                        fontName: 'Albert Sans'
                    },
                    // Set text direction to vertical
                    slantedText: false,
                    // Allow text wrapping
                    allowContainerBoundaryTextCutoff: false,
                    // Set maximum lines for wrapping
                    maxAlternation: 1,
                    // Force vertical alignment
                    textPosition: 'out',
                    // Control text spacing
                    minTextSpacing: 5,
                    // Show all labels
                    showTextEvery: 1
                } : {},
            }
        },
    }
}
